import {
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ForwardedRef,
    type PointerEvent,
} from "react";
import {
    EmailEditor,
    type EmailEditorProps,
    type EmailEditorRef,
} from "@react-email/editor";
import { StarterKit } from "@react-email/editor/extensions";
import { Placeholder } from "@tiptap/extension-placeholder";
import type { Extensions } from "@tiptap/core";
import { Gapcursor } from "@tiptap/extension-gapcursor";
import { EllipsisVertical } from "lucide-react";
import { Button, Menu } from "@/ui";
import { SuggestedTimesNode } from "./editor/SuggestedTimesNode";
import { EmptyBlockKeys } from "./editor/emptyBlockKeys";
import { KeepEmailTheme } from "./editor/keepEmailTheme";
import { EMAIL_BLOCK_NODES, STARTER_KIT_OVERRIDES, emailTheming } from "./editor/emailBlocks";
import { captureEditorPos, type EditorPos } from "../lib/editor-insert";

/**
 * EmailEditor REPLACES its entire base extension set when you pass `extensions`
 * (it does `extensionsProp ?? [StarterKit, Placeholder, EmailTheming]`), rather
 * than merging. To register our custom draggable node we therefore have to
 * rebuild that same default base and append the node — passing only the node
 * would strip paragraphs, marks, lists, etc. and break the editor.
 *
 * This mirrors EmailEditor's internal default (the image extension is still
 * appended by EmailEditor itself when onUploadImage is set, so we leave it out).
 */
function buildExtensions(opts: {
    placeholder?: string;
    theme?: EmailEditorProps["theme"];
    extra?: Extensions;
}): Extensions {
    // If a caller ever passes its own `extensions`, honour it verbatim and just
    // append our node (caller opted out of the default base on purpose).
    if (opts.extra) return [...opts.extra, Gapcursor, EmptyBlockKeys, KeepEmailTheme, SuggestedTimesNode];

    return [
        StarterKit.configure({
            // Keep the compose caret when clicking toolbar/menus/modals.
            // The default (true) jumps selection to the start of the doc on
            // blur, so signatures and suggested-times insert in the wrong place.
            FocusScopes: { clearSelectionOnBlur: false },
            // TrailingNode re-adds an empty paragraph after a trailing quote
            // or signature whenever it's deleted, so drafts kept growing
            // blank lines the user couldn't remove. Gapcursor still lets the
            // caret land after a final block.
            TrailingNode: false,
            // Stock button/quote/divider swapped for spaced versions below.
            ...STARTER_KIT_OVERRIDES,
        }),
        ...EMAIL_BLOCK_NODES,
        Placeholder.configure({
            placeholder:
                opts.placeholder ??
                (({ node }) =>
                    node.type.name === "heading"
                        ? `Heading ${node.attrs.level}`
                        : "Press '/' for commands"),
            includeChildren: true,
        }),
        emailTheming(opts.theme),
        Gapcursor,
        EmptyBlockKeys,
        KeepEmailTheme,
        SuggestedTimesNode,
    ];
}

type TextSize = "normal" | "small" | "tiny";

const TEXT_SIZE_STYLES: Record<Exclude<TextSize, "normal">, { fontSize: string; lineHeight: string }> = {
    // Tighter line-heights so small/tiny lines (e.g. the Email/Phone/Office
    // rows in a signature) sit closer together rather than spaced like body text.
    small: { fontSize: "12px", lineHeight: "14px" },
    tiny: { fontSize: "10px", lineHeight: "12px" },
};

function setForwardedRef(ref: ForwardedRef<EmailEditorRef>, value: EmailEditorRef | null) {
    if (typeof ref === "function") ref(value);
    else if (ref) ref.current = value;
}

export function withTextSize(style: string, size: TextSize): string {
    const declarations = style
        .split(";")
        .map(part => part.trim())
        .filter(Boolean)
        .filter(part => {
            const property = part.split(":", 1)[0]?.trim().toLowerCase();
            return property !== "font-size" && property !== "line-height";
        });

    if (size !== "normal") {
        const textStyle = TEXT_SIZE_STYLES[size];
        declarations.push(
            `font-size:${textStyle.fontSize}`,
            `line-height:${textStyle.lineHeight}`,
        );
    }

    return declarations.length > 0 ? `${declarations.join(";")};` : "";
}

/** Extra props beyond the underlying EmailEditor's — toolbar-only triggers. */
type RichEmailEditorProps = EmailEditorProps & {
    onContentChange?: (html: string, text: string) => void;
    /** When set, renders an "Insert times" toolbar button that calls this
     *  with the caret position captured on pointer-down (before blur). */
    onInsertSuggestedTimes?: (pos: EditorPos | null) => void;
};

const RichEmailEditor = forwardRef<EmailEditorRef, RichEmailEditorProps>(
    function RichEmailEditor({ onContentChange, onReady, onUploadImage, onInsertSuggestedTimes, extensions, placeholder, theme, ...props }, forwardedRef) {
        // Rebuild EmailEditor's default extension base + our draggable node,
        // memoised so the extension manager isn't rebuilt every render.
        const mergedExtensions = useMemo(
            () => buildExtensions({ placeholder, theme, extra: extensions }),
            [placeholder, theme, extensions],
        );
        const [editorRef, setEditorRef] = useState<EmailEditorRef | null>(null);
        const insertTimesPosRef = useRef<EditorPos | null>(null);
        const contentChangeRef = useRef(onContentChange);
        contentChangeRef.current = onContentChange;
        useEffect(() => {
            const editor = editorRef?.editor;
            if (!editor) return;
            const update = () => contentChangeRef.current?.(editor.getHTML(), editor.getText());
            editor.on("update", update);
            return () => { editor.off("update", update); };
        }, [editorRef]);


        const attachRef = useCallback((value: EmailEditorRef | null) => {
            setEditorRef(value);
            setForwardedRef(forwardedRef, value);
        }, [forwardedRef]);

        function applyTextSize(size: TextSize) {
            const editor = editorRef?.editor;
            if (!editor) return;

            const currentStyle = String(editor.getAttributes("preservedStyle").style ?? "");
            const nextStyle = withTextSize(currentStyle, size);
            const chain = editor.chain().focus();

            if (nextStyle) chain.setMark("preservedStyle", { style: nextStyle }).run();
            else chain.unsetMark("preservedStyle").run();
        }

        function preserveEditorSelection(event: PointerEvent<HTMLButtonElement>) {
            // Pointer-down normally moves the ProseMirror selection into the
            // toolbar on iOS before click fires. Keep the range intact so the
            // formatting command applies to the selected text.
            event.preventDefault();
        }

        function runFormat(command: "bold" | "italic" | "bulletList" | "orderedList" | "undo" | "redo") {
            const editor = editorRef?.editor;
            if (!editor) return;
            const chain = editor.chain().focus();
            if (command === "bold") chain.toggleBold().run();
            else if (command === "italic") chain.toggleItalic().run();
            else if (command === "bulletList") chain.toggleBulletList().run();
            else if (command === "orderedList") chain.toggleOrderedList().run();
            else if (command === "undo") chain.undo().run();
            else chain.redo().run();
        }

        // Show the image-size controls only while an image node is selected.
        const [imageSelected, setImageSelected] = useState(false);
        useEffect(() => {
            const editor = editorRef?.editor;
            if (!editor) return;
            const update = () => setImageSelected(editor.isActive("image"));
            update();
            editor.on("selectionUpdate", update);
            editor.on("transaction", update);
            return () => {
                editor.off("selectionUpdate", update);
                editor.off("transaction", update);
            };
        }, [editorRef]);

        // Email images are sized via the <img width> attribute (the one thing
        // every client — including Outlook — honors). Pixel widths for the
        // discrete sizes; "100%" for full-width/responsive.
        function applyImageWidth(width: string) {
            const editor = editorRef?.editor;
            if (!editor) return;
            editor.chain().focus().updateAttributes("image", { width }).run();
        }

        // The toolbar lives inside resizable focus panes, so viewport media
        // queries cannot tell us what actually fits. Measure the toolbar itself
        // and progressively move lower-priority groups into the overflow menu.
        const toolbarRef = useRef<HTMLDivElement>(null);
        const [toolbarWidth, setToolbarWidth] = useState(1000);
        useEffect(() => {
            const toolbar = toolbarRef.current;
            if (!toolbar) return;
            const update = () => setToolbarWidth(toolbar.getBoundingClientRect().width);
            update();
            const observer = new ResizeObserver(update);
            observer.observe(toolbar);
            return () => observer.disconnect();
        }, []);

        const showHistory = toolbarWidth >= 310;
        const showTextSizes = toolbarWidth >= 475;
        const showInlineImage = toolbarWidth >= 620;
        const showSuggestedTimes = toolbarWidth >= 760;
        const showImageSizes = toolbarWidth >= 900;
        const hasOverflow = !showHistory
            || !showTextSizes
            || Boolean(onUploadImage && !showInlineImage)
            || Boolean(onInsertSuggestedTimes && !showSuggestedTimes)
            || Boolean(imageSelected && !showImageSizes);

        const imageSizes = [
            ["Small", "200"],
            ["Medium", "400"],
            ["Large", "600"],
            ["Full", "100%"],
        ] as const;

        return (
            <div className="rich-email-editor">
                <div ref={toolbarRef} className="rich-email-editor__toolbar" role="toolbar" aria-label="Text formatting">
                    <button type="button" tabIndex={-1} onPointerDown={preserveEditorSelection} onClick={() => runFormat("bold")} disabled={!editorRef} title="Bold selected text" aria-label="Bold"><strong>B</strong></button>
                    <button type="button" tabIndex={-1} onPointerDown={preserveEditorSelection} onClick={() => runFormat("italic")} disabled={!editorRef} title="Italicize selected text" aria-label="Italic"><em>I</em></button>
                    <button type="button" tabIndex={-1} onPointerDown={preserveEditorSelection} onClick={() => runFormat("bulletList")} disabled={!editorRef} title="Bulleted list" aria-label="Bulleted list">• List</button>
                    <button type="button" tabIndex={-1} onPointerDown={preserveEditorSelection} onClick={() => runFormat("orderedList")} disabled={!editorRef} title="Numbered list" aria-label="Numbered list">1. List</button>
                    {showHistory && <button type="button" tabIndex={-1} onPointerDown={preserveEditorSelection} onClick={() => runFormat("undo")} disabled={!editorRef} title="Undo" aria-label="Undo">↶</button>}
                    {showHistory && <button type="button" tabIndex={-1} onPointerDown={preserveEditorSelection} onClick={() => runFormat("redo")} disabled={!editorRef} title="Redo" aria-label="Redo">↷</button>}
                    {showTextSizes && <span>Text size</span>}
                    {showTextSizes && (["normal", "small", "tiny"] as const).map(size => (
                        <button
                            key={size}
                            type="button"
                            // Out of the tab order: tabbing from Subject should land
                            // in the message body, not walk through the toolbar.
                            tabIndex={-1}
                            onPointerDown={preserveEditorSelection}
                            onClick={() => applyTextSize(size)}
                            disabled={!editorRef}
                            title={`Make selected text ${size}`}
                        >
                            {size[0].toUpperCase() + size.slice(1)}
                        </button>
                    ))}
                    {onUploadImage && showInlineImage && (
                        <button
                            type="button"
                            tabIndex={-1}
                            onPointerDown={preserveEditorSelection}
                            onClick={() => (editorRef?.editor?.commands as any)?.uploadImage?.()}
                            disabled={!editorRef}
                            title="Upload an image into the email body"
                        >
                            Inline image
                        </button>
                    )}
                    {onInsertSuggestedTimes && showSuggestedTimes && (
                        <button
                            type="button"
                            tabIndex={-1}
                            onPointerDown={event => {
                                preserveEditorSelection(event);
                                insertTimesPosRef.current = captureEditorPos(editorRef?.editor);
                            }}
                            onClick={() => onInsertSuggestedTimes(insertTimesPosRef.current)}
                            disabled={!editorRef}
                            title="Insert one-click booking buttons for a few open times"
                        >
                            <span aria-hidden="true" style={{ marginRight: 4 }}>🗓</span>
                            Insert times
                        </button>
                    )}
                    {imageSelected && showImageSizes && (
                        <span className="rich-email-editor__image-size" style={{ display: "inline-flex", gap: 4, alignItems: "center", marginLeft: 8, paddingLeft: 8, borderLeft: "1px solid var(--color-border)" }}>
                            <span>Image size</span>
                            {imageSizes.map(([label, width]) => (
                                <button
                                    key={label}
                                    type="button"
                                    tabIndex={-1}
                                    onPointerDown={preserveEditorSelection}
                                    onClick={() => applyImageWidth(width)}
                                    title={`Resize image to ${label.toLowerCase()}${width.endsWith("%") ? "" : ` (${width}px)`}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </span>
                    )}
                    {hasOverflow && (
                        <Menu.Root>
                            <Menu.Trigger
                                render={<Button variant="ghost" size="sm" className="rich-email-editor__more" aria-label="More formatting options" />}
                            >
                                <EllipsisVertical aria-hidden="true" />
                            </Menu.Trigger>
                            <Menu.Content align="end" className="rich-email-editor__overflow-menu">
                                {!showHistory && <Menu.Group>
                                    <Menu.GroupLabel>History</Menu.GroupLabel>
                                    <Menu.Item onClick={() => runFormat("undo")}>Undo</Menu.Item>
                                    <Menu.Item onClick={() => runFormat("redo")}>Redo</Menu.Item>
                                </Menu.Group>}
                                {!showTextSizes && <>
                                    <Menu.Separator />
                                    <Menu.Group>
                                        <Menu.GroupLabel>Text size</Menu.GroupLabel>
                                        {(["normal", "small", "tiny"] as const).map(size => (
                                            <Menu.Item key={size} onClick={() => applyTextSize(size)}>
                                                {size[0].toUpperCase() + size.slice(1)}
                                            </Menu.Item>
                                        ))}
                                    </Menu.Group>
                                </>}
                                {((onUploadImage && !showInlineImage) || (onInsertSuggestedTimes && !showSuggestedTimes)) && <>
                                    <Menu.Separator />
                                    <Menu.Group>
                                        <Menu.GroupLabel>Insert</Menu.GroupLabel>
                                        {onUploadImage && !showInlineImage && (
                                            <Menu.Item onClick={() => (editorRef?.editor?.commands as any)?.uploadImage?.()}>Inline image</Menu.Item>
                                        )}
                                        {onInsertSuggestedTimes && !showSuggestedTimes && (
                                            <Menu.Item
                                                onPointerDown={() => {
                                                    insertTimesPosRef.current = captureEditorPos(editorRef?.editor);
                                                }}
                                                onClick={() => onInsertSuggestedTimes(insertTimesPosRef.current)}
                                            >
                                                Suggested times
                                            </Menu.Item>
                                        )}
                                    </Menu.Group>
                                </>}
                                {imageSelected && !showImageSizes && <>
                                    <Menu.Separator />
                                    <Menu.Group>
                                        <Menu.GroupLabel>Image size</Menu.GroupLabel>
                                        {imageSizes.map(([label, width]) => (
                                            <Menu.Item key={label} onClick={() => applyImageWidth(width)}>{label}</Menu.Item>
                                        ))}
                                    </Menu.Group>
                                </>}
                            </Menu.Content>
                        </Menu.Root>
                    )}
                </div>
                <div className="rich-email-editor__content">
                    <EmailEditor
                        {...props}
                        placeholder={placeholder}
                        theme={theme}
                        extensions={mergedExtensions}
                        onUploadImage={onUploadImage}
                        ref={attachRef}
                        onReady={value => {
                            setEditorRef(value);
                            onReady?.(value);
                        }}
                    />
                </div>
            </div>
        );
    },
);

export default RichEmailEditor;
